"use client";

import { Leaf } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FinalAuditCTA() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const cards = [{
    heading: "Revenue Audit",
    description: "Get a comprehensive analysis of your current revenue streams and identify hidden opportunities for growth.",
    linkText: "Request Audit",
    to: "/HotelRevenueAuditFreePaidPage"
  }, {
    heading: "Consultation",
    description: "Schedule a one-on-one strategy session with Sunil Singh to discuss your property's specific revenue challenges.",
    linkText: "Book a Consultation",
    to: "/ContactPage"
  }];
  return <section data-section-id="165" id="finalauditcta" className="bg-slate-50 text-slate-900 relative overflow-hidden py-32 md:py-40 px-6">
      {}
      <div className="pointer-events-none absolute -bottom-20 -left-20 w-[600px] h-[600px] opacity-[0.04]" aria-hidden="true">
        <svg viewBox="0 0 600 600" fill="none" className="w-full h-full">
          <path d="M100 500 Q 150 400 200 380 Q 280 350 300 280 Q 320 200 380 180 Q 460 150 480 80" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M120 520 Q 180 460 240 440 Q 320 410 340 340" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <ellipse cx="200" cy="380" rx="30" ry="12" stroke="currentColor" strokeWidth="1" fill="none" transform="rotate(-30 200 380)" />
          <ellipse cx="300" cy="280" rx="35" ry="14" stroke="currentColor" strokeWidth="1" fill="none" transform="rotate(-45 300 280)" />
          <ellipse cx="380" cy="180" rx="30" ry="12" stroke="currentColor" strokeWidth="1" fill="none" transform="rotate(-60 380 180)" />
          <ellipse cx="240" cy="440" rx="28" ry="11" stroke="currentColor" strokeWidth="1" fill="none" transform="rotate(-25 240 440)" />
        </svg>
      </div>

      <div className="relative mx-auto max-w-[760px]">
        {}
        <div ref={headerRef} className={`flex flex-col items-center text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="mb-10" aria-hidden="true">
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" className="text-blue-900">
              <circle cx="30" cy="30" r="2" fill="currentColor" />
              <circle cx="30" cy="30" r="10" stroke="currentColor" strokeWidth="0.6" />
              <circle cx="30" cy="30" r="20" stroke="currentColor" strokeWidth="0.4" />
              <circle cx="30" cy="30" r="28" stroke="currentColor" strokeWidth="0.4" />
              {Array.from({
              length: 8
            }).map((_, i) => {
              const angle = i * 360 / 8;
              return <g key={i} transform={`rotate(${angle} 30 30)`}>
                    <line x1="30" y1="12" x2="30" y2="20" stroke="currentColor" strokeWidth="0.6" />
                    <circle cx="30" cy="10" r="1.5" stroke="currentColor" strokeWidth="0.5" fill="none" />
                  </g>;
            })}
              {Array.from({
              length: 16
            }).map((_, i) => {
              const angle = i * 360 / 16;
              return <g key={`d-${i}`} transform={`rotate(${angle} 30 30)`}>
                    <circle cx="30" cy="22" r="0.8" fill="currentColor" opacity="0.7" />
                  </g>;
            })}
            </svg>
          </div>

          {}
          <p className="font-default text-xs uppercase tracking-[0.3em] text-blue-900 mb-8">
            Take Action
          </p>

          {}
          <h2 className="font-default font-bold text-[40px] md:text-[52px] leading-[1.15] text-slate-900 mb-10">
            Maximize Your Hotel’s Revenue with <em className="italic font-light text-blue-900">Data-Driven</em> Strategies.
          </h2>

          {}
          <div className="space-y-6 text-base text-slate-700" style={{
          lineHeight: 1.8
        }}>
            <p>
              HotelRevBaBa provides expert revenue management consulting to help property owners and managers 
              optimize their OTA performance and increase direct bookings. Our data-centric approach ensures 
              your hotel stays competitive in an ever-evolving digital landscape.
            </p>
            <p>
              Whether you need a deep-dive revenue audit or ongoing strategic guidance, Sunil Singh and his 
              team are here to help you unlock your property's full financial potential.
            </p>
          </div>
        </div>

        {}
        <div ref={cardsRef} className={`mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-1000 delay-200 ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {cards.map((card, i) => <Card data-index={i} key={i} className="group relative bg-white border border-slate-200 rounded-lg p-8 md:p-10 transition-all duration-500 hover:-translate-y-1 hover:border-blue-900/30 hover:shadow-lg">
              <div className="flex flex-col items-start gap-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-blue-900/20 bg-blue-50">
                  <Leaf className="h-5 w-5 text-blue-900" strokeWidth={1.5} />
                </div>

                <h3 className="font-default text-2xl md:text-[28px] font-semibold text-slate-900">
                  {card.heading}
                </h3>

                <p className="text-sm text-slate-600" style={{
              lineHeight: 1.7
            }}>
                  {card.description}
                </p>

                <Button asChild variant="link" className="h-auto p-0 text-blue-900 hover:text-blue-700 font-default text-sm tracking-wide group/link">
                  <Link to={card.to}>
                    <span>{card.linkText}</span>
                    <span className="inline-block transition-transform duration-300 group-hover/link:translate-x-1">
                      &rarr;
                    </span>
                  </Link>
                </Button>
              </div>
            </Card>)}
        </div>
      </div>
    </section>;
}